import { useCallback, useEffect, useState } from 'react';
import { useHingeSelectClient } from '../../api';
import { CommitLogEntry } from '../context/types';
import { useWizardState } from '../context/wizard-state.context';
import { midpointIso } from '../utils/iso-to-protodate';

export interface ValidationCheck {
  id: string;
  label: string;
  request: unknown;
  status: 'pending' | 'pass' | 'fail';
  response?: unknown;
  error?: string;
  /** Optional repair-CTA target step. */
  repairStep?: string;
}

// Pulls IDs minted by successful create-* steps out of the commit log so
// validation calls hit real records instead of placeholder strings. Only the
// IDs the validation suite actually needs — keep the merchantLocation /
// merchant / organization extraction in the orchestrator's seedRuntimeFromLog.
const idsFromCommitLog = (
  log: CommitLogEntry[],
): {
  contractId?: string;
  feeScheduleId?: string;
  providerAccountId?: string;
} => {
  const out: {
    contractId?: string;
    feeScheduleId?: string;
    providerAccountId?: string;
  } = {};
  for (const e of log) {
    if (e.status !== 'success' || e.response == null) continue;
    const r = e.response as Record<string, unknown>;
    if (e.step === 'createContract') {
      out.contractId = (r.contract as { id?: string })?.id ?? out.contractId;
    } else if (e.step === 'createFeeSchedule') {
      out.feeScheduleId =
        (r.feeSchedule as { id?: string })?.id ?? out.feeScheduleId;
    } else if (e.step === 'createProviderAccount') {
      out.providerAccountId =
        (r.providerAccount as { providerAccountId?: string })
          ?.providerAccountId ?? out.providerAccountId;
    }
  }
  return out;
};

export const useValidationSuite = (): {
  checks: ValidationCheck[];
  running: boolean;
  rerun: () => void;
} => {
  const { state } = useWizardState();
  const client = useHingeSelectClient();
  const [checks, setChecks] = useState<ValidationCheck[]>([]);
  const [running, setRunning] = useState(false);
  const [tick, setTick] = useState(0);

  const rerun = useCallback((): void => {
    setTick(t => t + 1);
  }, []);

  useEffect(() => {
    if (state.input == null || !state.commit.completed) return;
    setRunning(true);
    setChecks([]);

    const seeded = idsFromCommitLog(state.commit.log);
    // PR 2 Slice 1: per-TIN decisions, canonical-TIN read for the suite.
    const canonicalTin = state.input.locations[0]?.tax_id ?? '';
    const ca = state.decisions.customerAccount[canonicalTin];
    const customerAccountId =
      ca?.mode === 'reuse' ? ca.id : seeded.providerAccountId;
    const contractId = seeded.contractId;
    const feeScheduleId = seeded.feeScheduleId;
    const contractDecision = state.decisions.contract[canonicalTin];
    const start = contractDecision?.effectiveStart ?? '';
    const end = contractDecision?.effectiveEnd ?? start;
    const serviceDate = midpointIso(start, end);

    interface CheckSpec {
      seed: ValidationCheck;
      run: () => Promise<ValidationCheck>;
    }
    const checkSpecs: CheckSpec[] = [];

    for (const loc of state.input.locations) {
      const lk = `${loc.address.locality}|${loc.address.address_line_1}`;
      const mlId =
        state.resolutions.merchantLocations[lk]?.data?.merchantLocationId;
      if (mlId == null) continue;
      const id = `fs-for-loc:${loc.address.locality}`;
      const label = `Fee schedule resolves for ${loc.address.locality}`;
      const req = { merchantLocationId: mlId, serviceDate };
      checkSpecs.push({
        seed: { id, label, request: req, status: 'pending' },
        run: async () => {
          try {
            const result = await client.getFeeScheduleForServiceProvider(req);
            if (!result.ok) {
              return {
                id,
                label,
                request: req,
                status: 'fail',
                error: result.reason,
                repairStep: 'csps',
              };
            }
            return {
              id,
              label,
              request: req,
              status: 'pass',
              response: result.data,
            };
          } catch (e) {
            return {
              id,
              label,
              request: req,
              status: 'fail',
              error: (e as Error).message,
              repairStep: 'csps',
            };
          }
        },
      });
    }

    if (contractId != null) {
      const req = { id: contractId };
      checkSpecs.push({
        seed: {
          id: 'getContract',
          label: 'Contract is readable',
          request: req,
          status: 'pending',
        },
        run: async () => {
          try {
            const r = await client.getContract(req);
            return {
              id: 'getContract',
              label: 'Contract is readable',
              request: req,
              status: 'pass',
              response: r,
            };
          } catch (e) {
            return {
              id: 'getContract',
              label: 'Contract is readable',
              request: req,
              status: 'fail',
              error: (e as Error).message,
              repairStep: 'contract',
            };
          }
        },
      });
    }

    if (feeScheduleId != null) {
      const req = { id: feeScheduleId };
      checkSpecs.push({
        seed: {
          id: 'getFeeSchedule',
          label: 'Fee schedule is readable',
          request: req,
          status: 'pending',
        },
        run: async () => {
          try {
            const r = await client.getFeeSchedule(req);
            return {
              id: 'getFeeSchedule',
              label: 'Fee schedule is readable',
              request: req,
              status: 'pass',
              response: r,
            };
          } catch (e) {
            return {
              id: 'getFeeSchedule',
              label: 'Fee schedule is readable',
              request: req,
              status: 'fail',
              error: (e as Error).message,
              repairStep: 'fee-schedule',
            };
          }
        },
      });
    }

    if (customerAccountId != null) {
      const req = { customerAccountId };
      checkSpecs.push({
        seed: {
          id: 'listContractsForCA',
          label: 'Customer account contracts list',
          request: req,
          status: 'pending',
        },
        run: async () => {
          try {
            const r = await client.listContractsForCustomerAccount(req);
            return {
              id: 'listContractsForCA',
              label: `Customer account has ${r.contracts.length} contract${r.contracts.length === 1 ? '' : 's'}`,
              request: req,
              status: 'pass',
              response: r,
            };
          } catch (e) {
            return {
              id: 'listContractsForCA',
              label: 'Customer account contracts list',
              request: req,
              status: 'fail',
              error: (e as Error).message,
            };
          }
        },
      });
    }

    if (contractId != null) {
      const req = { contractId };
      checkSpecs.push({
        seed: {
          id: 'getCSPs',
          label: 'Contract service providers list',
          request: req,
          status: 'pending',
        },
        run: async () => {
          try {
            const r = await client.getContractServiceProviders(req);
            return {
              id: 'getCSPs',
              label: `Contract has ${r.contractServiceProviders.length} service provider${r.contractServiceProviders.length === 1 ? '' : 's'}`,
              request: req,
              status: 'pass',
              response: r,
            };
          } catch (e) {
            return {
              id: 'getCSPs',
              label: 'Contract service providers list',
              request: req,
              status: 'fail',
              error: (e as Error).message,
              repairStep: 'csps',
            };
          }
        },
      });

      checkSpecs.push({
        seed: {
          id: 'getContractHistory',
          label: 'Contract history',
          request: req,
          status: 'pending',
        },
        run: async () => {
          try {
            const r = await client.getContractHistory(req);
            return {
              id: 'getContractHistory',
              label: `Contract has ${r.contractVersions.length} version${r.contractVersions.length === 1 ? '' : 's'}`,
              request: req,
              status: 'pass',
              response: r,
            };
          } catch (e) {
            return {
              id: 'getContractHistory',
              label: 'Contract history',
              request: req,
              status: 'fail',
              error: (e as Error).message,
            };
          }
        },
      });
    }

    // Seed pending rows so the Done page can render skeletons for each
    // outstanding check while Promise.all awaits.
    setChecks(checkSpecs.map(s => s.seed));
    void Promise.all(checkSpecs.map(s => s.run())).then(results => {
      setChecks(results);
      setRunning(false);
    });
    // tick is a manual rerun signal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, tick, state.commit.completed]);

  return { checks, running, rerun };
};
